/**
 * Operator responsibility ("Betreiberverantwortung") — admin curation surface
 * over the tenant-neutral Legal.* layer (LEGAL-4).
 *
 * Two jobs:
 *  1. Visualize regulation → provision → obligation → regulated object so a
 *     domain expert can see what is in the database and how it connects.
 *  2. Adjust the LLM conclusions — confirm, correct or reject extracted /
 *     synthesized obligations, including old-vs-new diff review for proposed
 *     changes. Every state change is attributable and append-only.
 *
 * Posture: documented diligence, not an oracle — unconfirmed LLM output is
 * never presented as authoritative, coverage gaps are surfaced prominently.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useOverlayBotPage } from "../../components/overlay-bot/OverlayBotContext";

import { useModalContext } from "../../components/ModalProvider";
import Section from "../../components/Section";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import ReviewQueuePanel from "./ReviewQueuePanel";
import ObligationReviewPanel from "./ObligationReviewPanel";
import DiffReviewPanel from "./DiffReviewPanel";
import RegulationBrowserPanel from "./RegulationBrowserPanel";
import VocabularyPanel from "./VocabularyPanel";
import DerivedLayerPanel from "./DerivedLayerPanel";
import CoveragePanel from "./CoveragePanel";
import HazardTypesPanel from "./HazardTypesPanel";
import HazardReleasesPanel from "./HazardReleasesPanel";
import HazardCatalogPanel from "./HazardCatalogPanel";
import ContextRulesPanel from "./ContextRulesPanel";
import SeveritySimulatorPanel from "./SeveritySimulatorPanel";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Properties {
  /**
   * `full` — Administration (Plattform-Root): alle Reiter.
   * `rulebook` — Seite „Regelwerke“ für Fachprüfer mit Plattformrolle aus beliebigen Mandanten:
   * Prüfliste, Änderungsprüfung und Regelwerke; nur die Routen, die der Server ihnen öffnet.
   */
  scope?: "full" | "rulebook";
}

const RULEBOOK_TABS = ["reviewQueue", "diffReview", "regulations"];

const summaryCount = (rows: any[] | undefined, key: string): number =>
  (rows || []).find((row: any) => row._id === key)?.count || 0;

/** Eine Kennzahl der Kopfzeile: Zahl in der Statusfarbe, Bezeichnung daneben. */
function HeaderCount(properties: {
  count: number;
  label: string;
  tone: "warning" | "success" | "destructive";
  title?: string;
}) {
  const toneClass =
    properties.tone === "success"
      ? "text-success"
      : properties.tone === "warning"
        ? "text-warning"
        : "text-destructive";

  return (
    <div className="min-w-0 break-words" title={properties.title}>
      <span className={`font-bold tabular-nums ${toneClass}`}>{properties.count}</span>{" "}
      {properties.label}
    </div>
  );
}

/** Adressparameter der geöffneten Einzelansicht — neben `tab` der zweite Sichtwähler. */
const OBLIGATION_PARAM = "obligation";

export default function OperatorResponsibilityPanel(properties: Properties) {
  const rulebookOnly = properties.scope === "rulebook";
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const [, setSearchParams] = useSearchParams();

  // Betreiberverantwortung is where the vocabulary is least obvious — severity,
  // context shift, band. The assistant is told what the reader is looking at so
  // a question about "der Basiswert" does not have to be spelled out.
  useOverlayBotPage({
    helpText: t('overlayBot.help.operatorResponsibility'),
    context:
      'Der Nutzer betrachtet im Portal die Betreiberverantwortung: Gefährdungen, ' +
      'Kontextregeln und die Simulation der Schwere.',
  });


  const allowedTab = (value: string | null) =>
    value && (!rulebookOnly || RULEBOOK_TABS.includes(value)) ? value : "reviewQueue";
  const [tab, setTab] = useState(() => allowedTab(new URL(window.location.href).searchParams.get("tab")));
  const [summary, setSummary] = useState<any>(null);
  const [vocabulary, setVocabulary] = useState<any>(null);
  const [regulations, setRegulations] = useState<any[]>([]);
  const [regulatedObjects, setRegulatedObjects] = useState<any[]>([]);
  // Die geöffnete Pflicht steht in der Adresse, nicht nur im Zustand: So erzeugt das Öffnen einen
  // Historieneintrag, „Zurück" führt auf die Liste statt aus ihr heraus, und die Einzelansicht ist
  // verlinkbar.
  const [openedObligationKey, setOpenedObligationKey] = useState<string | null>(
    () => new URL(window.location.href).searchParams.get(OBLIGATION_PARAM),
  );
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [openedHazardType, setOpenedHazardType] = useState<string | null>(null);
  const [hazardReviewCode, setHazardReviewCode] = useState<string | null>(null);
  // Obligations with no hazard attached — the counter has to reach zero, so it
  // stays on the tab bar while it is above it (§5).
  const [withoutHazardCount, setWithoutHazardCount] = useState(0);

  const loadSharedData = async () => {
    try {
      const [summaryResult, vocabularyResult, regulationsResult, regulatedObjectsResult] = await Promise.all([
        legalAPI.getSummary(),
        legalAPI.getVocabulary(),
        legalAPI.getRegulations(),
        legalAPI.getRegulatedObjects(),
      ]);

      setSummary(summaryResult);
      setVocabulary(vocabularyResult);
      setRegulations(regulationsResult || []);
      setRegulatedObjects(regulatedObjectsResult || []);
    } catch (error) {
      pushError(error);
    }

    // Separate from the block above: the hazard layer is newer than the rest of
    // this panel and must not take the shared data down with it if it fails.
    if (rulebookOnly) return;
    try {
      const withoutHazard = await legalAPI.getObligationsWithoutHazard(1);
      setWithoutHazardCount(withoutHazard?.count || 0);
    } catch (error) {
      pushError(error);
    }
  };

  useEffect(() => {
    loadSharedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  useEffect(() => {
    const handlePopState = () => {
      const url = new URL(window.location.href);
      const newTab = allowedTab(url.searchParams.get("tab"));
      setTab(newTab);
      // Ohne diese Zeile blieb die Einzelansicht beim Zurückgehen stehen, während die Adresse
      // schon wieder die der Liste war — der Zustand, in dem die Filter verloren gingen.
      setOpenedObligationKey(url.searchParams.get(OBLIGATION_PARAM));
      setOpenedHazardType(null);
      setHazardReviewCode(null);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const refresh = () => setRefreshSignal((value) => value + 1);

  const pendingCount = summaryCount(summary?.obligationsByStatus, "pending");
  const confirmedCount = summaryCount(summary?.obligationsByStatus, "confirmed");
  const gapCount =
    summaryCount(summary?.regulationsByCoverage, "pending") +
    summaryCount(summary?.regulationsByCoverage, "notCovered");

  /**
   * Pflicht öffnen und schließen über die Adresse — ein Historieneintrag je Richtung, damit der
   * Zurück-Knopf auf die Liste führt statt aus ihr heraus.
   *
   * Grundlage ist der Router-Stand. Das war eine Zeit lang nicht so: Solange das ReviewQueuePanel
   * seine Filter per `history.replaceState` am Router vorbei schrieb, musste hier die tatsächliche
   * Adresszeile gelesen werden, sonst gingen sie verloren. Seit die Filter denselben Weg nehmen,
   * ist der Router die eine Wahrheit und die Ausnahme kann weg.
   */
  const changeOpenedObligation = (obligationKey: string | null) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (obligationKey) next.set(OBLIGATION_PARAM, obligationKey);
        else next.delete(OBLIGATION_PARAM);
        return next;
      },
      { replace: false },
    );

    setOpenedObligationKey(obligationKey);
  };

  const changeTab = (value: string) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("tab", value);
        return next;
      },
      // Ein Eintrag in der Historie: Der Zurück-Knopf führt auf den vorherigen Reiter (der
      // popstate-Effekt oben liest ihn wieder ein).
      { replace: false },
    );

    setTab(value);
  };

  if (openedObligationKey) {
    return (
      <ObligationReviewPanel
        obligationKey={openedObligationKey}
        vocabulary={vocabulary}
        regulatedObjects={regulatedObjects}
        showHazards={!rulebookOnly}
        onClose={(didChange: boolean) => {
          changeOpenedObligation(null);

          if (didChange) {
            refresh();
          }
        }}
      />
    );
  }

  const hazardTypesPanel = (
    <HazardTypesPanel
      refreshSignal={refreshSignal}
      openCode={openedHazardType}
      onOpenCode={setOpenedHazardType}
      onStartReview={(code: string) => {
        setOpenedHazardType(null);
        setHazardReviewCode(code);
        changeTab("hazardReleases");
      }}
      onChanged={refresh}
    />
  );

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <h2 className="min-w-0 break-words text-lg font-semibold">
          {rulebookOnly ? t("rulebookPage.title") : t("operatorResponsibilityPanel.title")}
        </h2>
        <div className="flex min-w-0 flex-wrap gap-x-6 gap-y-1 text-sm">
          <HeaderCount
            count={pendingCount}
            label={t("operatorResponsibilityPanel.pendingReviews")}
            tone="warning"
          />
          <HeaderCount
            count={confirmedCount}
            label={t("operatorResponsibilityPanel.confirmedObligations")}
            tone="success"
          />
          <HeaderCount
            count={gapCount}
            label={t("operatorResponsibilityPanel.coverageGapsShort")}
            tone="destructive"
          />
          {summary?.staleChecklists > 0 ? (
            <HeaderCount
              count={summary.staleChecklists}
              label={t("operatorResponsibilityPanel.staleChecklists")}
              tone="destructive"
            />
          ) : null}
          {/* An obligation nobody could map to a hazard is the strongest signal
              that the catalogue is incomplete — visible until it is zero. */}
          {withoutHazardCount > 0 ? (
            <HeaderCount
              count={withoutHazardCount}
              label={t("operatorResponsibilityPanel.withoutHazard")}
              title={t("operatorResponsibilityPanel.withoutHazardHint")}
              tone="destructive"
            />
          ) : null}
        </div>
      </div>

      <p className="min-w-0 break-words text-sm text-muted-foreground">
        {rulebookOnly ? t("rulebookPage.subtitle") : t("operatorResponsibilityPanel.subtitle")}
      </p>

      <Tabs value={tab} onValueChange={changeTab} className="min-w-0">
        <TabsList>
          <TabsTrigger value="reviewQueue">
            {t("operatorResponsibilityPanel.tabs.reviewQueue")}
          </TabsTrigger>
          <TabsTrigger value="diffReview">
            {t("operatorResponsibilityPanel.tabs.diffReview")}
          </TabsTrigger>
          <TabsTrigger value="regulations">
            {t("operatorResponsibilityPanel.tabs.regulations")}
          </TabsTrigger>
          {rulebookOnly ? null : (
            <>
          <TabsTrigger value="vocabulary">
            {t("operatorResponsibilityPanel.tabs.vocabulary")}
          </TabsTrigger>
          <TabsTrigger value="derived">
            {t("operatorResponsibilityPanel.tabs.derived")}
          </TabsTrigger>
          <TabsTrigger value="coverage">
            {t("operatorResponsibilityPanel.tabs.coverage")}
          </TabsTrigger>
          {/* Hazards before releases: the way in is usually a concrete type.
              Anyone arriving from a notification lands in releases directly. */}
          <TabsTrigger value="hazards">
            {t("operatorResponsibilityPanel.tabs.hazards")}
          </TabsTrigger>
          <TabsTrigger value="hazardReleases">
            {t("operatorResponsibilityPanel.tabs.hazardReleases")}
          </TabsTrigger>
          <TabsTrigger value="hazardCatalog">
            {t("operatorResponsibilityPanel.tabs.hazardCatalog")}
          </TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="reviewQueue">
          <ReviewQueuePanel
            vocabulary={vocabulary}
            regulations={regulations}
            regulatedObjects={regulatedObjects}
            refreshSignal={refreshSignal}
            onOpenObligation={changeOpenedObligation}
            onBatchConfirmed={refresh}
          />
        </TabsContent>

        <TabsContent value="diffReview">
          <DiffReviewPanel
            vocabulary={vocabulary}
            regulatedObjects={regulatedObjects}
            refreshSignal={refreshSignal}
            onReviewed={refresh}
          />
        </TabsContent>

        <TabsContent value="regulations">
          <RegulationBrowserPanel
            regulations={regulations}
            refreshSignal={refreshSignal}
            onOpenObligation={changeOpenedObligation}
          />
        </TabsContent>

        <TabsContent value="vocabulary">
          <VocabularyPanel vocabulary={vocabulary} refreshSignal={refreshSignal} />
        </TabsContent>

        <TabsContent value="derived">
          <DerivedLayerPanel refreshSignal={refreshSignal} onOpenObligation={changeOpenedObligation} />
        </TabsContent>

        <TabsContent value="coverage">
          <CoveragePanel refreshSignal={refreshSignal} />
        </TabsContent>

        <TabsContent value="hazards">
          {/* Rule set and simulator come first, the type list below. They
              are sections rather than tabs of their own — there are three
              rules, and reviewing them means jumping between the rule and
              its effect. Putting them after a 100-row worklist would have
              meant scrolling past it to reach the tool the review needs. */}
          {!openedHazardType ? (
            <div className="flex min-w-0 flex-col gap-6">
              <Section title={t("context.rules.title")}>
                <ContextRulesPanel refreshSignal={refreshSignal} onChanged={refresh} />
              </Section>
              <Section title={t("context.simulator.title")}>
                <SeveritySimulatorPanel refreshSignal={refreshSignal} />
              </Section>
              <Section title={t("hazard.types.sectionTypes")}>{hazardTypesPanel}</Section>
            </div>
          ) : (
            // Drilldown replaces the whole tab: the sections above would
            // only compete with the type being reviewed.
            hazardTypesPanel
          )}
        </TabsContent>

        <TabsContent value="hazardReleases">
          <HazardReleasesPanel
            refreshSignal={refreshSignal}
            reviewCode={hazardReviewCode}
            onReviewCode={setHazardReviewCode}
            onChanged={refresh}
          />
        </TabsContent>

        <TabsContent value="hazardCatalog">
          <HazardCatalogPanel
            refreshSignal={refreshSignal}
            onOpenType={(code: string) => {
              setOpenedHazardType(code);
              changeTab("hazards");
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
